import { watch, onMounted, onBeforeUnmount, unref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const RESPONSE_TIMEOUT = 120000;
const ERROR_MESSAGES = {
  failed: 'WGPT_CUSTOM_REPORTS.CHAT.ERROR',
  timeout: 'WGPT_CUSTOM_REPORTS.CHAT.TIMEOUT',
  processing: 'WGPT_CUSTOM_REPORTS.CHAT.PROCESSING',
};

// Wires the Report Builder async lifecycle for a view: client-side timeout
// fallback, translated error toast, and reconnect recovery. `reportId` is the
// scope key ('new' on the create page, the report id on the edit page).
export function useReportBuilderAsync(reportId) {
  const store = useStore();
  const { t } = useI18n();
  const uiFlags = useMapGetter('wgptCustomReports/getUIFlags');

  let timer = null;
  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  const startTimer = () => {
    clearTimer();
    timer = setTimeout(() => {
      store.dispatch('wgptCustomReports/handleTimeout');
    }, RESPONSE_TIMEOUT);
  };

  watch(
    () => uiFlags.value.isChatting,
    isChatting => (isChatting ? startTimer() : clearTimer())
  );

  watch(
    () => uiFlags.value.error,
    error => {
      if (!error) return;
      useAlert(t(ERROR_MESSAGES[error] || ERROR_MESSAGES.failed));
      store.dispatch('wgptCustomReports/clearError');
    }
  );

  const onReconnect = () => {
    if (uiFlags.value.isChatting) {
      store.dispatch('wgptCustomReports/resyncChat', unref(reportId));
    }
  };

  onMounted(() => {
    store.dispatch('wgptCustomReports/setChatReportId', unref(reportId));
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, onReconnect);
  });

  onBeforeUnmount(() => {
    clearTimer();
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, onReconnect);
  });
}
